// A rupiah amount typed into a price cell: staff's costs and a reseller's retail prices. It holds
// a Dinero, not text: digits only, grouped with dots as they are typed, empty for "not offered".
// Whole rupiah: sen are not typed (a saved amount with sen shows them, and loses them once the
// cell is retyped). type="text" with a numeric keypad rather than type="number", which takes "e"
// and "-", spins on the wheel and cannot group.
import { parseMoney, type Dinero } from "@kilat/contracts/money";
import { useLayoutEffect, useRef, type ComponentProps } from "react";
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from "@/components/ui/input-group";
import { formatAmount } from "./money";

/** 13 digits of rupiah: under the largest whole number of sen a Dinero<number> holds exactly. */
const MAX_DIGITS = 13;

type Props = Omit<ComponentProps<"input">, "value" | "onChange" | "type"> & {
  value: Dinero<number> | null;
  onChange: (value: Dinero<number> | null) => void;
};

/** The rupiah in what the cell shows: the digits before any comma (the sen are dropped). */
const rupiah = (text: string) => (text.split(",")[0] ?? "").replace(/\D/g, "");

/** Where the caret goes in `shown` to sit after `digits` digits. */
function caretAfter(shown: string, digits: number): number {
  if (digits === 0) return 0;
  let seen = 0;
  for (let i = 0; i < shown.length; i++) {
    if (/\d/.test(shown[i]!) && ++seen === digits) return i + 1;
  }
  return shown.length;
}

export function MoneyInput({ value, onChange, ...props }: Props) {
  const ref = useRef<HTMLInputElement>(null);
  // How many digits sat before the caret when the cell last changed, to put it back after grouping.
  const caret = useRef<number | null>(null);
  const shown = value === null ? "" : formatAmount(value);

  useLayoutEffect(() => {
    const input = ref.current;
    if (caret.current === null || !input || document.activeElement !== input) return;
    const at = caretAfter(shown, caret.current);
    input.setSelectionRange(at, at);
    caret.current = null;
  });

  function change(text: string, selection: number | null) {
    const digits = rupiah(text).replace(/^0+(?=\d)/, "");
    if (digits.length > MAX_DIGITS) return;
    caret.current = rupiah(text.slice(0, selection ?? text.length)).length;
    onChange(digits === "" ? null : parseMoney(`${digits}.00`));
  }

  return (
    <InputGroup>
      <InputGroupAddon>
        <InputGroupText>Rp</InputGroupText>
      </InputGroupAddon>
      <InputGroupInput
        {...props}
        ref={ref}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={shown}
        onChange={(event) => change(event.target.value, event.target.selectionStart)}
      />
    </InputGroup>
  );
}
